/**
 * Base44 API client – wired to your backend.
 * Uses httpOnly cookies for auth (credentials: 'include'). No token in JS.
 *
 * All entities expose:
 *   - list()    -> GET    /resource
 *   - create()  -> POST   /resource
 *   - get(id)   -> GET    /resource/:id
 *   - update(id, data) -> PUT /resource/:id
 *   - delete(id)       -> DELETE /resource/:id
 *
 * Base URL is read from Vite env:
 *   VITE_API_BASE_URL=http://localhost:5000
 */

const API_BASE =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || 'http://localhost:5000'

/**
 * Resolve a file URL to an absolute URL for fetch/download.
 * Always uses API_BASE for /api/uploads/ paths so images work regardless of
 * wrong host/protocol stored from backend (e.g. behind proxy).
 */
export function resolveFileUrl(url) {
  if (!url || typeof url !== 'string') return url
  if (url.startsWith('data:')) return url
  // Extract filename from any /api/uploads/ path and rebuild with API_BASE
  const uploadsMatch = String(url).match(/\/api\/uploads\/([a-zA-Z0-9._-]+)(?:\?|$)/)
  if (uploadsMatch) {
    return `${API_BASE}/api/uploads/${uploadsMatch[1]}`
  }
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  const path = url.startsWith('/') ? url : `/api/uploads/${url}`
  return `${API_BASE}${path}`
}

/** MIME type to file extension for images and common file types */
const MIME_TO_EXT = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/bmp': '.bmp',
  'image/tiff': '.tiff',
  'image/svg+xml': '.svg',
  'application/pdf': '.pdf',
  'application/octet-stream': '', // server may use this for forced download; keep ext from URL
}

function extFromMime(mime) {
  if (!mime || typeof mime !== 'string') return ''
  const m = mime.toLowerCase().trim().split(';')[0]
  return MIME_TO_EXT[m] || ''
}

/**
 * Download a file from URL. Uses protected /api/download with direct link (no CORS).
 * Navigation requests bypass CORS; cookies are sent for same-site/subdomain auth.
 * Works for all image types (JPEG, PNG, GIF, WebP, etc.) and PDFs.
 * @param {string} url - Resolved file URL (or data: URL)
 * @param {string} filename - Suggested download filename
 * @returns {Promise<boolean>} - true if download initiated, false if failed
 */
export async function downloadFile(url, filename) {
  const notify = (msg) => {
    if (typeof window !== 'undefined' && window.addNotification) {
      window.addNotification(msg, 'error')
    }
  }
  try {
    if (!url || typeof url !== 'string') {
      notify('Download URL is invalid')
      return false
    }
    if (url.startsWith('data:')) {
      const mimeMatch = url.match(/^data:([^;]+);/)
      const mime = mimeMatch ? mimeMatch[1] : ''
      const ext = extFromMime(mime) || '.bin'
      const baseName = (filename || 'download').replace(/\.[^.]+$/, '') || 'download'
      const finalFilename = baseName + ext
      const res = await fetch(url)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = finalFilename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
      return true
    }
    const uploadsMatch = String(url).match(/\/api\/uploads\/([a-zA-Z0-9._-]+)(?:\?|$)/)
    const fileFilename = uploadsMatch ? uploadsMatch[1] : url.split('/').pop()?.split('?')[0]
    if (!fileFilename) {
      notify('Download URL is invalid')
      return false
    }
    const extFromUrl = fileFilename.includes('.') ? '.' + fileFilename.split('.').pop().toLowerCase() : ''
    const baseName = filename && /^[a-zA-Z0-9._-]+$/.test(filename)
      ? filename.replace(/\.[^.]+$/, '') || filename
      : fileFilename.replace(/\.[^.]+$/, '') || fileFilename
    let finalFilename = extFromUrl ? baseName + extFromUrl : (filename || fileFilename)
    const uploadsUrl = new URL(`${API_BASE}/api/uploads/${fileFilename}`)
    uploadsUrl.searchParams.set('download', '1')
    uploadsUrl.searchParams.set('filename', finalFilename)
    const downloadUrlStr = uploadsUrl.toString()

    // Direct link click — navigation (no CORS). Server sends Content-Disposition: attachment.
    const link = document.createElement('a')
    link.href = downloadUrlStr
    link.download = finalFilename
    link.rel = 'noopener noreferrer'
    link.target = '_blank'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return true
  } catch (err) {
    notify(err?.message || 'Download failed')
    return false
  }
}

function handle401() {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register') && !window.location.pathname.startsWith('/forgot-password') && !window.location.pathname.startsWith('/reset-password')) {
    window.location.href = '/login'
  }
}

function authHeaders(includeJson = true) {
  const h = {}
  if (includeJson) h['Content-Type'] = 'application/json'
  return h
}

/**
 * Helper that unwraps the common `{ success, data }` shape
 * used by the backend. Adjust if your responses differ.
 */
async function handleResponse(response) {
  const json = await response.json().catch(() => null)

  if (!response.ok) {
    if (response.status === 401) {
      handle401()
    }
    const message =
      (json && (json.message || json.error)) ||
      `Request failed with status ${response.status}`
    throw new Error(message)
  }

  // Paginated responses: return full object { data, pagination }
  if (json && typeof json === 'object' && 'pagination' in json) {
    return json
  }

  // Standard responses: unwrap { success, data }
  if (json && typeof json === 'object' && 'data' in json) {
    return json.data
  }

  return json
}

function createResourceClient(path) {
  const basePath = path.startsWith('/') ? path : `/${path}`

  return {
    async list(params) {
      const url = new URL(`${API_BASE}${basePath}`)
      if (params != null) {
        if (typeof params === 'string') {
          url.searchParams.set('sort', params)
        } else if (typeof params === 'object') {
          Object.entries(params).forEach(([key, value]) => {
            if (value != null) url.searchParams.set(key, String(value))
          })
        }
      }

      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(false),
      })
      return handleResponse(res)
    },

    async create(payload) {
      const res = await fetch(`${API_BASE}${basePath}`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload ?? {}),
      })
      return handleResponse(res)
    },

    async get(id) {
      if (!id) throw new Error('id is required for get()')
      const res = await fetch(`${API_BASE}${basePath}/${id}`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(false),
      })
      return handleResponse(res)
    },

    async update(id, payload) {
      if (!id) throw new Error('id is required for update()')
      const res = await fetch(`${API_BASE}${basePath}/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload ?? {}),
      })
      return handleResponse(res)
    },

    async delete(id) {
      if (!id) throw new Error('id is required for delete()')
      const res = await fetch(`${API_BASE}${basePath}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(false),
      })
      return handleResponse(res)
    },
  }
}

// Map your logical entities to real backend routes.
// Adjust the paths below if your backend uses different URLs.
const userClient = createResourceClient('/api/users')

export const base44 = {
  integrations: {
    Core: {
      async UploadFile({ file }) {
        const formData = new FormData()
        formData.append('file', file)
        const res = await fetch(`${API_BASE}/api/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        })
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          const message = json?.message || `Upload failed (${res.status})`
          throw new Error(message)
        }
        return json.data || json
      },
    },
  },
  entities: {
    Apartment: {
      ...createResourceClient('/api/apartments'),
      // delete()/create() above still work as before. These three call the
      // archive/restore endpoints so a bulk delete + undo doesn't have to
      // fan out into one HTTP request per record.
      async restore(id) {
        if (!id) throw new Error('id is required for restore()')
        const res = await fetch(`${API_BASE}/api/apartments/${id}/restore`, {
          method: 'PATCH',
          headers: authHeaders(false),
          credentials: 'include',
        })
        return handleResponse(res)
      },
      async bulkDelete(ids) {
        const res = await fetch(`${API_BASE}/api/apartments/bulk-delete`, {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify({ ids }),
        })
        return handleResponse(res)
      },
      async bulkRestore(ids) {
        const res = await fetch(`${API_BASE}/api/apartments/bulk-restore`, {
          method: 'POST',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify({ ids }),
        })
        return handleResponse(res)
      },
    },
    ApartmentReservation: createResourceClient('/api/apartment-reservations'),
    Client: {
      ...createResourceClient('/api/clients'),
      async restore(id) {
        if (!id) throw new Error('id is required for restore()')
        const res = await fetch(`${API_BASE}/api/clients/${id}/restore`, {
          method: 'PATCH',
          headers: authHeaders(false),
          credentials: 'include',
        })
        return handleResponse(res)
      },
    },
    Sale: {
      ...createResourceClient('/api/sales'),
      async restore(id) {
        if (!id) throw new Error('id is required for restore()')
        const res = await fetch(`${API_BASE}/api/sales/${id}/restore`, {
          method: 'PATCH',
          headers: authHeaders(false),
          credentials: 'include',
        })
        return handleResponse(res)
      },
    },
    Payment: {
      ...createResourceClient('/api/payments'),
      async restore(id) {
        if (!id) throw new Error('id is required for restore()')
        const res = await fetch(`${API_BASE}/api/payments/${id}/restore`, {
          method: 'PATCH',
          headers: authHeaders(false),
          credentials: 'include',
        })
        return handleResponse(res)
      },
    },
    Receipt: createResourceClient('/api/receipts'),
    DefaultedSale: {
      ...createResourceClient('/api/defaulted-sales'),
      async restore(id) {
        if (!id) throw new Error('id is required for restore()')
        const res = await fetch(`${API_BASE}/api/defaulted-sales/${id}/restore`, {
          method: 'PATCH',
          headers: authHeaders(false),
          credentials: 'include',
        })
        return handleResponse(res)
      },
    },
    User: {
      ...userClient,
      async updateRole(id, role, verifyPassword) {
        const res = await fetch(`${API_BASE}/api/users/${id}/role`, {
          method: 'PATCH',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify({ role, verifyPassword }),
        })
        return handleResponse(res)
      },
      async changePassword(id, verifyPassword, newPassword) {
        const res = await fetch(`${API_BASE}/api/users/${id}/password`, {
          method: 'PATCH',
          headers: authHeaders(),
          credentials: 'include',
          body: JSON.stringify({ verifyPassword, newPassword }),
        })
        return handleResponse(res)
      },
    },
  },
}
